import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type ClassValue, FCC, cn } from '@eluve/components';

export type OverlayPosition = 'left' | 'center' | 'right';
export type OverlayState = 'collapsed' | 'expanded' | 'fullsize';
export type OverlayVariant =
  | 'default'
  | 'brand'
  | 'gray'
  | 'teal'
  | 'warning'
  | 'destructive';

export type ContentRegistration = {
  className?: ClassValue;
  heightClassName?: ClassValue;
  position?: OverlayPosition;
  radiusClassName?: ClassValue;
  variant?: OverlayVariant;
  widthClassName?: ClassValue;
};

export type CardContextValue = {
  currentContent: string;
  state: OverlayState;
  register: (id: string, config: ContentRegistration) => void;
  unregister: (id: string) => void;
  requestCollapse: () => void;
  requestFullsize: () => void;
  lockExpanded: (key: string) => void;
  unlockExpanded: (key: string) => void;
};

export const FloatingActionCardContext = createContext<CardContextValue | null>(
  null,
);

export const useFloatingActionCard = () => {
  const context = useContext(FloatingActionCardContext);
  if (!context) {
    throw new Error(
      'useFloatingActionCard must be used within a FloatingActionCard',
    );
  }
  return context;
};

type FloatingActionCardProps = {
  'aria-label'?: string;
  currentContent: string;
  disableInteraction?: boolean;
  expandTrigger?: 'hover' | 'click';
  isHidden?: boolean;
  onStateChange?: (state: OverlayState) => void;
  position: OverlayPosition;
  state?: OverlayState;
  variant: OverlayVariant;
};

export const FloatingActionCard: FCC<FloatingActionCardProps> = ({
  'aria-label': ariaLabel,
  children,
  currentContent,
  disableInteraction = false,
  expandTrigger = 'hover',
  isHidden = false,
  onStateChange,
  position: defaultPosition,
  state: controlledState,
  variant: defaultVariant,
}) => {
  const [internalState, setInternalState] = useState<OverlayState>('collapsed');
  const [registrations, setRegistrations] = useState(
    new Map<string, ContentRegistration>(),
  );

  const isControlled = controlledState !== undefined;
  const state = isControlled ? controlledState : internalState;

  const locksRef = useRef(new Set<string>());
  const pointerInsideRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isLocked = () => locksRef.current.size > 0;

  const register = useCallback((id: string, config: ContentRegistration) => {
    setRegistrations((prev) => new Map(prev).set(id, config));
  }, []);

  const unregister = useCallback((id: string) => {
    setRegistrations((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const changeState = useCallback(
    (newState: OverlayState) => {
      if (!isControlled) {
        setInternalState(newState);
      }
      onStateChange?.(newState);
    },
    [isControlled, onStateChange],
  );

  const lockExpanded = useCallback((key: string) => {
    locksRef.current.add(key);
  }, []);

  const unlockExpanded = useCallback((key: string) => {
    locksRef.current.delete(key);
  }, []);

  const requestFullsize = useCallback(() => {
    if (disableInteraction) {
      return;
    }

    if (state !== 'fullsize') {
      changeState('fullsize');
    }
  }, [disableInteraction, state, changeState]);

  const requestCollapse = useCallback(() => {
    if (disableInteraction || isLocked()) {
      return;
    }

    if (state !== 'collapsed') {
      changeState('collapsed');
    }
  }, [disableInteraction, state, changeState]);

  const handlePointerEnter = () => {
    pointerInsideRef.current = true;

    if (disableInteraction || expandTrigger === 'click') {
      return;
    }

    if (state === 'collapsed') {
      changeState('expanded');
    }
  };

  const handlePointerLeave = () => {
    pointerInsideRef.current = false;

    if (disableInteraction || expandTrigger === 'click') {
      return;
    }

    if (state === 'expanded' && !isLocked()) {
      changeState('collapsed');
    }
  };

  const handleClick = () => {
    if (disableInteraction || expandTrigger !== 'click') {
      return;
    }

    if (state === 'collapsed') {
      changeState('expanded');
    }
  };

  useEffect(() => {
    if (disableInteraction && state !== 'collapsed') {
      locksRef.current.clear();
      changeState('collapsed');
    } else if (
      !disableInteraction &&
      expandTrigger !== 'click' &&
      pointerInsideRef.current &&
      state === 'collapsed'
    ) {
      changeState('expanded');
    }
  }, [disableInteraction, expandTrigger, state, changeState]);

  useEffect(() => {
    if (expandTrigger !== 'click' || state !== 'expanded') {
      return undefined;
    }

    const handleDocumentMouseDown = (event: Event) => {
      if (isLocked()) return;
      const container = containerRef.current;
      if (container && event.composedPath().includes(container)) return;
      changeState('collapsed');
    };

    const root = containerRef.current?.getRootNode() ?? document;
    root.addEventListener('mousedown', handleDocumentMouseDown, {
      capture: true,
    });
    return () => {
      root.removeEventListener('mousedown', handleDocumentMouseDown, {
        capture: true,
      });
    };
  }, [expandTrigger, state, changeState]);

  const isCollapsed = state === 'collapsed';
  const isExpanded = state === 'expanded';
  const isFullsize = state === 'fullsize';

  const activeConfig = registrations.get(currentContent);

  const position = activeConfig?.position ?? defaultPosition;
  const variant = activeConfig?.variant ?? defaultVariant;
  const width = activeConfig?.widthClassName ?? 'w-11';
  const height = activeConfig?.heightClassName ?? 'h-11';
  const radius = activeConfig?.radiusClassName ?? 'rounded-[1.375rem]';
  const className = activeConfig?.className;

  const geometry = cn(
    'absolute bottom-0 transition-all duration-300 ease-in-out',
    position === 'left' && 'left-0',
    position === 'center' && 'left-1/2',
    position === 'center' &&
      (isFullsize ? '-translate-x-[calc(50%-0.5rem)]' : '-translate-x-1/2'),
    position === 'right' && 'right-0',
    width,
    height,
    isCollapsed && `z-10 ${radius}`,
    isExpanded && `z-20 ${radius}`,
    isFullsize && `z-30 ${radius} -m-2`,
    isHidden && 'opacity-0',
  );

  const coloredShadow = cn(
    variant === 'brand' && 'shadow-bg-indigo-strong-primary/5',
    variant === 'teal' && 'shadow-text-teal-subtle/5',
    variant === 'warning' && 'shadow-warning/5',
    variant === 'destructive' && 'shadow-destructive/5',
  );

  const contextValue = useMemo(
    () => ({
      currentContent,
      state,
      register,
      unregister,
      requestCollapse,
      requestFullsize,
      lockExpanded,
      unlockExpanded,
    }),
    [
      currentContent,
      state,
      register,
      unregister,
      requestCollapse,
      requestFullsize,
      lockExpanded,
      unlockExpanded,
    ],
  );

  return (
    <FloatingActionCardContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        aria-label={ariaLabel}
        onClick={handleClick}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className={cn(
          geometry,
          'pointer-events-auto overflow-hidden shadow-lg',
          variant === 'default' &&
            'bg-bg-primary ring-1 ring-black/5 ring-offset-0',
          variant === 'brand' &&
            'bg-bg-indigo-strong-primary ring-1 ring-black/5 ring-inset',
          variant === 'gray' &&
            'bg-bg-strong-primary ring-1 ring-black/5 ring-inset',
          variant === 'teal' &&
            'bg-text-teal-subtle ring-1 ring-black/5 ring-inset',
          variant === 'warning' &&
            'bg-warning-foreground ring-1 ring-black/5 ring-inset',
          variant === 'destructive' &&
            'bg-destructive-foreground ring-1 ring-black/5 ring-inset',
          isHidden && 'pointer-events-none',
          className,
        )}
      >
        {children}
      </div>
      <div
        aria-hidden="true"
        className={cn(
          geometry,
          'pointer-events-none shadow-lg',
          coloredShadow || 'opacity-0',
          className,
        )}
      />
    </FloatingActionCardContext.Provider>
  );
};
